import React from 'react';
import { Box, Typography, Divider, useMediaQuery, useTheme } from '@material-ui/core';
import { ReportCardComponentProps } from '../schema-types';
import {
  PlayerIconWrapper,
  PositionBannerWrapper,
  PositionBannerLeft,
  PositionBannerCenter,
  PositionBannerRight,
  Highlight
} from '../../../Score/ReportCard/report-card.helper-components';
import { Bold } from '../../../_core/_ui/typography.component';
import { substituteVariables } from './text-utils';
import { filterDOMProps } from '../utils/prop-normalizer';
import imgColorLogo from '../../../../assets/logo-colour.png';
import imgPlayer from '../../../../assets/player.png';

export const LegacyHeaderComponent: React.FC<ReportCardComponentProps> = ({
  data,
  player,
  playerAssessment,
  baseData,
  reportCardLevel: _reportCardLevel,
  context: _context,
  organizationLogo,
  organizationLogoHeight,
  ...props
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Default texts that can be overridden by data
  const defaultTexts = {
    title: 'Report Card',
    description: 'This athlete report card includes evaluation results for over twenty key attributes related to skills, tactics and game play. This feedback will inform and help guide the long term development for {playerName}.',
    privateResultsText: 'Your private evaluation results are listed below.',
    paymentDescriptionPrefix: 'The',
    paymentDescriptionMiddle: 'Board of Directors has opted to make the report card portion of this evaluation an optional purchase for all of its members. As a result,',
    paymentDescriptionSuffix: '\'s athlete report card is available for a fee of',
    paymentInstructionsPrefix: 'To view the evaluation results, simply enter your credit card details below and',
    paymentInstructionsSuffix: 'click "Buy & View Results"'
  };

  // Merge default texts with provided data
  const texts = { ...defaultTexts, ...data };

  // Determine logo height: data > organizationLogoHeight prop > default '80px'
  console.log("Logo height", {data, organizationLogoHeight, props})
  const logoHeight = data?.organizationLogoHeight || organizationLogoHeight || '80px';

  // Determine logo to use (organization logo takes precedence)
  const logoElement = React.useMemo(() => {
    const logoStyle = { height: logoHeight, width: 'auto', objectFit: 'contain' as const };

    if (organizationLogo) {
      // If organizationLogo is a React node, use it directly
      if (typeof organizationLogo === 'string') {
        return <img style={logoStyle} src={organizationLogo} alt="Organization Logo" />;
      } else {
        return organizationLogo;
      }
    } else if (playerAssessment?.assessment?.company?.logo) {
      return <img style={logoStyle} src={playerAssessment.assessment.company.logo} alt="Organization Logo" />;
    } else {
      return <img style={logoStyle} src={imgColorLogo} alt="Max Analytics" />;
    }
  }, [organizationLogo, playerAssessment?.assessment?.company?.logo, logoHeight]);

  if (!player || !playerAssessment) return null;

  return (
    <Box {...filterDOMProps(props)}>
      {/* Logo and Report Card Title */}
      <Box display="flex" flexDirection="column" justifyContent="center">
        {logoElement}
        <Typography variant="h3" style={{ textAlign: 'center' }}>
          <Bold>{substituteVariables(texts.title, data, baseData, player, playerAssessment)}</Bold>
        </Typography>
      </Box>

      {/* Player Information Section */}
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        alignItems={isMobile ? "center" : "center"}
        mt={2}
      >
        <Box mr={isMobile ? 0 : 5} mb={isMobile ? 2 : 0}>
          <PlayerIconWrapper>
            <img src={imgPlayer} alt="Player" />
          </PlayerIconWrapper>
          <PositionBannerWrapper>
            <PositionBannerLeft />
            <PositionBannerCenter>{playerAssessment.position.name}</PositionBannerCenter>
            <PositionBannerRight />
          </PositionBannerWrapper>
        </Box>
        <Box textAlign={isMobile ? "center" : "left"}>
          <Typography variant="h2">
            <Bold>{player.firstName} {player.lastName}</Bold>
          </Typography>
          <Typography variant="h5">{playerAssessment.assessment.company.name}</Typography>
          <Typography variant="h6">
            {playerAssessment.assessment.name}, {playerAssessment.ageGroup.name}
          </Typography>
        </Box>
      </Box>

      <Box my={2}>
        <Divider />
      </Box>

      {/* Report Card Description */}
      <Box textAlign="center" mb={6}>
        <Typography gutterBottom>
          {substituteVariables(texts.description, data, baseData, player, playerAssessment)
            .split('{playerName}').map((part, index, array) => (
              index < array.length - 1 ? (
                <React.Fragment key={index}>
                  {part}
                  <Bold>{player.firstName} {player.lastName}</Bold>
                </React.Fragment>
              ) : part
            ))}
        </Typography>

        {(!playerAssessment.ageGroup.showReportPayment || playerAssessment.paymentRef) ? (
            <Typography>
              <i>{substituteVariables(texts.privateResultsText, data, baseData, player, playerAssessment)}</i>
            </Typography>
          ) :
          (
            <Box mb={4}>
              <Typography gutterBottom>
                {substituteVariables(texts.paymentDescriptionPrefix, data, baseData, player, playerAssessment)}{' '}
                <Highlight>{playerAssessment.assessment.company.name}</Highlight>{' '}
                {substituteVariables(texts.paymentDescriptionMiddle, data, baseData, player, playerAssessment)}{' '}
                <Highlight>{player.firstName} {player.lastName}</Highlight>
                {substituteVariables(texts.paymentDescriptionSuffix, data, baseData, player, playerAssessment)}{' '}
                <Highlight>${(playerAssessment.ageGroup.reportPrice / 100).toFixed(2)} + GST</Highlight>.
              </Typography>

              <Typography>
                {substituteVariables(texts.paymentInstructionsPrefix, data, baseData, player, playerAssessment)}<br />
                <Highlight>{substituteVariables(texts.paymentInstructionsSuffix, data, baseData, player, playerAssessment)}</Highlight>
              </Typography>
            </Box>
          )
        }
      </Box>
    </Box>
  );
};
